<template>
  <sf-calculator-stats>
    <template slot-scope="{ data }">
      <div>
        <div>{{ $t('calculator.principal') }}</div>
        <div v-if="isApplicationInfo && clientApplication" v-qa="'calculator-amount'">
          {{ clientApplication.principalAmount.amount | moneyWithDecimal }}
        </div>
        <div v-else v-qa="'calculator-amount'">
          {{ data.principal | moneyWithDecimal }}
        </div>
      </div>

      <div v-if="withDate">
        <div>{{ $t('calculator.repay-date') }}</div>
        <div v-if="isApplicationInfo" v-qa="'calculator-endDate'">
          {{ clientApplication.repaymentDate | date }}
        </div>
        <div v-else v-qa="'calculator-endDate'">
          {{ data.endDate | date }}
        </div>
      </div>

      <div v-if="withInsurance">
        <div>
          {{ $t('offer-increase-page.payment-info-insurance') }}
          <span v-if="isVisibleDiscount">*</span>
        </div>

        <div class="discount">
          <div
            v-if="isVisibleDiscount"
            v-qa="`calculator-insurance-without-discount`"
            class="discount__without">
            {{ insuranceWithoutDiscountAmount }}
          </div>
          <div v-qa="`calculator-insurance`" class="discount__with">
            {{ insuranceAmount }}
          </div>
        </div>
      </div>

      <template v-if="isRewardInfoVisible">
        <div v-for="item in currencyItems" :key="item.key">
          <div>{{ item.title }}<span v-if="isVisibleDiscount">*</span></div>

          <div class="discount">
            <div
              v-if="isVisibleDiscount"
              v-qa="`calculator-${item.key}-without-discount`"
              class="discount__without">
              {{ item.withoutDiscountData | moneyWithDecimal }}
            </div>
            <div v-qa="`calculator-${item.key}`" class="discount__with">
              {{ item.discountData | moneyWithDecimal }}
            </div>
          </div>
        </div>

        <span v-if="isVisibleDiscount" class="description">
          * {{ $t('calculator.description') }}
        </span>
      </template>
    </template>
  </sf-calculator-stats>
</template>

<script>
import { mapGetters } from 'vuex';
import { WEB_ROUTES } from '~/utils/webConfig';
import { zeroAmount } from '~/utils/consts';

const CALCULATOR_STATS = {
  INTEREST: 'interest',
  TOTAL: 'total',
};

export default {
  name: 'CalculatorStats',
  props: {
    isApplicationInfo: {
      type: Boolean,
      required: true,
    },
    withInsurance: {
      type: Boolean,
      default: false,
    },
    withDate: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapGetters('arithmetic', ['displayed']),
    isVisibleDiscount() {
      return this.displayed && this.displayed.all && this.displayed.all.hasDiscount;
    },
    clientApplication() {
      return this.$client && this.$client.application;
    },
    insuranceAmount() {
      const amount = this.$sr('displayed.all.insurance.amount');
      return amount ? this.$options.filters.moneyWithDecimal(amount) : zeroAmount;
    },
    insuranceWithoutDiscountAmount() {
      const amount = this.$sr('all.insurance.amountWithoutDiscount', this.displayed);
      return amount ? this.$options.filters.moneyWithDecimal(amount) : zeroAmount;
    },
    currencyItems() {
      return [
        {
          key: CALCULATOR_STATS.INTEREST,
          title: this.$t('calculator.interest'),
          discountData: this.isApplicationInfo
            ? this.clientApplication.interestAmount.amount
            : this.displayed.interest,
          withoutDiscountData: this.displayed.interestWithoutDiscount,
        },
        {
          key: CALCULATOR_STATS.TOTAL,
          title: this.$t('calculator.total'),
          discountData: this.isApplicationInfo
            ? this.clientApplication.totalRepayableAmount.amount
            : this.displayed.total,
          withoutDiscountData: this.displayed.totalWithoutDiscount,
        },
      ];
    },
    isRewardInfoVisible() {
      const { name } = this.$route;
      return ![
        WEB_ROUTES.MAIN,
        WEB_ROUTES.LANDING_GA,
        WEB_ROUTES.REGISTRATION_STEP_1,
        WEB_ROUTES.REGISTRATION_STEP_2,
        WEB_ROUTES.REGISTRATION_STEP_3,
        WEB_ROUTES.REGISTRATION_STEP_4,
        WEB_ROUTES.APPLY,
        WEB_ROUTES.HOW_TO_APPLY,
        WEB_ROUTES.HOW_TO_REPAY,
        WEB_ROUTES.FAQ,
        WEB_ROUTES.PROFILE_MY_LOAN,
        WEB_ROUTES.PROFILE_NO_LOAN,
        WEB_ROUTES.COMPLAINS,
        WEB_ROUTES.ABOUT,
      ].includes(`${name}`.split('___')[0]);
    },
  },
};
</script>

<style scoped lang="scss">
.discount {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  &__without {
    color: map_get($colors, error);
    font-size: 12px;
    font-weight: 400;
    margin-right: 10px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 6px;
      width: 100%;
      height: 1px;
      background-color: map_get($colors, error);
      transform: rotate(-8deg) translateX(-3px);
    }
  }
}

.description {
  font-size: 12px;
  line-height: 15px;
  font-weight: 400;
  color: map-get($colors, error);
  text-decoration: underline;
  margin-bottom: 10px;
}

/deep/ .calculator__stats__rows {
  span {
    color: map_get($colors, error);
  }

  > * {
    height: auto;
    min-height: 40px;
  }
}
</style>
